// One JSON blob in localStorage for every piece of intro / hint /
// onboarding-orientation state. Replaces eight ad-hoc keys
// (`bb_first_run_tour_seen_v3`, `bb_show_helpcue`, `bb_hint_*`, ...)
// with a single `bb_intro_state` so:
//
//   - resetting all hints (Profile → "Show app intro again") is one
//     localStorage.removeItem call,
//   - inspection during QA shows the whole intro state at a glance,
//   - new orientation surfaces add a key to the schema rather than
//     a new localStorage entry.
//
// The shape:
//
//   {
//     tour_seen:  boolean,   // FirstRunTour finished or capped
//     later_n:    number,    // "Maybe later" dismissals so far
//     helpcue:    boolean,   // pulse the home `?` button once
//     player_coached: boolean, // in-player first-session overlay shown
//     hints: {               // per-route one-time hints
//       calendar: boolean,
//       learning: boolean,
//       profile:  boolean,
//       session:  boolean,
//       method:   boolean,
//     },
//     whatsnew_seen_id: string,
//   }
//
// Reads default missing fields, so older blobs survive a schema add.
//
// On first read, migrates legacy single-purpose keys into the blob
// and removes them so the storage stays clean.

const KEY = 'bb_intro_state';

// Legacy keys that may still be sitting in a returning user's
// localStorage; migrated on first read. Kept as a small map so a
// future cleanup PR can drop the migration once analytics confirm
// nobody has them anymore.
const LEGACY = {
  tour_seen:        ['bb_first_run_tour_seen_v3', 'bb_first_run_tour_seen_v2', 'bb_first_run_tour_seen'],
  later_n:          ['bb_first_run_tour_later_n'],
  helpcue:          ['bb_show_helpcue'],
  whatsnew_seen_id: ['bb_whatsnew_seen_id'],
};
const LEGACY_HINTS = {
  calendar: 'bb_hint_calendar',
  learning: 'bb_hint_learning',
  profile:  'bb_hint_profile',
  session:  'bb_hint_session',
  method:   'bb_hint_method',
};

function emptyState() {
  return {
    tour_seen: false,
    later_n: 0,
    helpcue: false,
    player_coached: false,
    hints: { calendar: false, learning: false, profile: false, session: false, method: false },
    whatsnew_seen_id: '',
  };
}

let migrated = false;

function migrateOnce(state) {
  if (migrated) return state;
  migrated = true;
  let dirty = false;
  try {
    for (const [field, keys] of Object.entries(LEGACY)) {
      for (const k of keys) {
        const v = localStorage.getItem(k);
        if (v == null) continue;
        if (field === 'later_n') {
          const n = Number(v) || 0;
          if (n > state.later_n) { state.later_n = n; dirty = true; }
        } else if (field === 'whatsnew_seen_id') {
          if (!state.whatsnew_seen_id) { state.whatsnew_seen_id = v; dirty = true; }
        } else if (v === 'true') {
          if (!state[field]) { state[field] = true; dirty = true; }
        }
        localStorage.removeItem(k);
      }
    }
    for (const [field, key] of Object.entries(LEGACY_HINTS)) {
      const v = localStorage.getItem(key);
      if (v == null) continue;
      if (v === 'true' && !state.hints[field]) { state.hints[field] = true; dirty = true; }
      localStorage.removeItem(key);
    }
  } catch {}
  if (dirty) {
    try { localStorage.setItem(KEY, JSON.stringify(state)); } catch {}
  }
  return state;
}

export function getIntroState() {
  let raw = null;
  try { raw = localStorage.getItem(KEY); } catch {}
  let state = emptyState();
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      state = { ...state, ...parsed, hints: { ...state.hints, ...(parsed?.hints || {}) } };
    } catch {}
  }
  return migrateOnce(state);
}

export function patchIntroState(patch) {
  const cur = getIntroState();
  const next = {
    ...cur,
    ...patch,
    hints: { ...cur.hints, ...(patch?.hints || {}) },
  };
  try { localStorage.setItem(KEY, JSON.stringify(next)); } catch {}
  return next;
}

export function resetIntroState() {
  try { localStorage.removeItem(KEY); } catch {}
  // Also clear legacy keys defensively, in case migration never ran.
  try {
    for (const keys of Object.values(LEGACY)) keys.forEach(k => localStorage.removeItem(k));
    Object.values(LEGACY_HINTS).forEach(k => localStorage.removeItem(k));
  } catch {}
  migrated = false;
}

// Convenience reads — components can call these directly without
// destructuring the blob.

export function isTourSeen()      { return getIntroState().tour_seen; }
export function isHelpcueArmed()  { return getIntroState().helpcue; }
export function laterCount()      { return getIntroState().later_n; }
export function isHintSeen(name)  { return !!getIntroState().hints?.[name]; }
export function isPlayerCoached() { return getIntroState().player_coached; }
export function whatsNewSeenId()  { return getIntroState().whatsnew_seen_id || ''; }

export function markTourSeen() {
  return patchIntroState({ tour_seen: true, helpcue: true });
}
export function bumpLater() {
  const n = laterCount() + 1;
  return patchIntroState({ later_n: n });
}
export function clearHelpcue() {
  return patchIntroState({ helpcue: false });
}
export function markHintSeen(name) {
  return patchIntroState({ hints: { [name]: true } });
}
export function markPlayerCoached() {
  return patchIntroState({ player_coached: true });
}
export function markWhatsNewSeen(id) {
  return patchIntroState({ whatsnew_seen_id: id || '' });
}
